import { inject } from '@angular/core';
import { TranslationService } from '@lib/i18n/data-access';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ToastService } from '@platform-workspace/design-system-v2';
import { UploadWidgetItemStatus } from '@shared/utils/enums';
import { downloadBlob, getContentDispositionFilename } from '@shared/utils/helpers';
import { BaseResponse, RegisterUploadParams, UploadWidgetItemDto } from '@shared/utils/interfaces';
import { hideLoading, showLoading, withFeatureLoading } from '@shared/utils/models';
import { catchError, EMPTY, exhaustMap, finalize, from, map, mergeMap, Observable, of, pipe, switchMap, tap } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { OpfsService, UploadStrategyResolverService, UploadWidgetApiService } from '../../services';
import {
  DEFAULT_REPORT_FILE_NAME,
  INITIAL_STATE,
  RESUMABLE_STATUSES,
  RETAINED_FILE_STATUSES,
  STATUS_TOAST_VARIANT,
  SUCCESSFUL_STATUSES,
} from './upload-widget.constant';
import { setRecordStatus, showSectionToast } from './upload-widget.util';

export const UploadWidgetStore = signalStore(
  {
    providedIn: 'root',
  },
  withState(INITIAL_STATE),
  withFeatureLoading(),
  withMethods(
    (
      store,
      opfsService = inject(OpfsService),
      strategyResolver = inject(UploadStrategyResolverService),
      toastService = inject(ToastService),
      translationService = inject(TranslationService),
    ) => ({
      openWidget: (): void => {
        patchState(store, { isWidgetVisible: true });
      },

      closeWidget: (): void => {
        patchState(store, { isWidgetVisible: false });
      },

      /** Reads the persisted file back from disk, transfers it, then cleans up. */
      startUpload: rxMethod<UploadWidgetItemDto>(
        pipe(
          tap((record) => patchState(store, setRecordStatus(store, record.uuid, UploadWidgetItemStatus.Uploading))),
          mergeMap((record) =>
            from(opfsService.getFile(record.uuid)).pipe(
              switchMap((file) => strategyResolver.resolve(record.section).upload(file, record)),
              switchMap((response) => {
                const status = response.payload?.status ?? UploadWidgetItemStatus.Completed;

                if (!SUCCESSFUL_STATUSES.has(status)) {
                  return of(response);
                }

                return from(opfsService.deleteFile(record.uuid)).pipe(map(() => response));
              }),
              tapResponse({
                next: (response) => {
                  const status = response.payload?.status ?? UploadWidgetItemStatus.Completed;
                  patchState(store, setRecordStatus(store, record.uuid, status));

                  const variant = STATUS_TOAST_VARIANT[status];
                  if (variant) {
                    const successCount = (response.payload as { successCount?: number } | undefined)?.successCount ?? 0;
                    showSectionToast(toastService, translationService, record.section, variant, { count: successCount });
                  }
                },
                error: (error: unknown) => {
                  patchState(store, setRecordStatus(store, record.uuid, UploadWidgetItemStatus.Failed));
                  console.error(error);
                },
              }),
            ),
          ),
        ),
      ),
    }),
  ),
  withMethods(
    (
      store,
      uploadWidgetApiService = inject(UploadWidgetApiService),
      opfsService = inject(OpfsService),
      toastService = inject(ToastService),
      translationService = inject(TranslationService),
    ) => ({
      /**
       * Loads the upload history on app init, purges any locally persisted file that is
       * no longer needed (orphaned or belonging to a finished record), then resumes any
       * transfer that never finished (INITIATED / UPLOADING) and whose file is still on disk.
       */
      loadRecords: rxMethod<void>(
        pipe(
          switchMap(() => {
            patchState(store, showLoading());

            return uploadWidgetApiService.getRecords().pipe(
              switchMap((response) => {
                const records = response.payload ?? [];
                patchState(store, { registeredUploads: records });

                return from(opfsService.listFiles()).pipe(
                  switchMap((fileNames) => {
                    const statusByUuid = new Map(records.map((record) => [record.uuid, record.status]));
                    const staleFiles = fileNames.filter((name) => {
                      const status = statusByUuid.get(name);
                      return status === undefined || !RETAINED_FILE_STATUSES.has(status);
                    });

                    return from(Promise.all(staleFiles.map((name) => opfsService.deleteFile(name).catch(() => undefined))));
                  }),
                  switchMap(() => {
                    const candidates = records.filter((record) => RESUMABLE_STATUSES.has(record.status));
                    if (!candidates.length) {
                      return EMPTY;
                    }

                    return from(Promise.all(candidates.map((record) => opfsService.exists(record.uuid)))).pipe(
                      tap((existsFlags) => {
                        const resumable = candidates.filter((_, index) => existsFlags[index]);
                        resumable.forEach((record) => store.startUpload(record));
                      }),
                    );
                  }),
                );
              }),
              catchError((error: unknown) => {
                console.error(error);
                return EMPTY;
              }),
              finalize(() => {
                patchState(store, hideLoading());
              }),
            );
          }),
        ),
      ),

      /** Clears the upload history on the backend, then wipes all locally persisted files. */
      clearHistory(): Observable<BaseResponse<void>> {
        patchState(store, showLoading());

        return uploadWidgetApiService.clearHistory().pipe(
          switchMap((response) =>
            from(opfsService.clearUploads()).pipe(
              catchError(() => of(undefined)),
              map(() => response),
            ),
          ),
          finalize(() => {
            patchState(store, hideLoading());
          }),
        );
      },

      /** Removes a single record on the backend, then deletes its locally persisted file. */
      removeRecord(uuid: string): Observable<BaseResponse<void>> {
        patchState(store, showLoading());

        return uploadWidgetApiService.removeRecord(uuid).pipe(
          switchMap((response) =>
            from(opfsService.deleteFile(uuid)).pipe(
              catchError(() => of(undefined)),
              map(() => response),
            ),
          ),
          finalize(() => {
            patchState(store, hideLoading());
          }),
        );
      },

      /** Downloads the upload report, naming the file from the `Content-Disposition` header. */
      downloadReport: rxMethod<string>(
        pipe(
          exhaustMap((uuid) => {
            patchState(store, showLoading());

            return uploadWidgetApiService.downloadReport(uuid).pipe(
              tapResponse({
                next: (response) => {
                  if (response.body) {
                    const filename = getContentDispositionFilename(response.headers.get('content-disposition')) ?? DEFAULT_REPORT_FILE_NAME;
                    downloadBlob(response.body, filename);
                  }
                },
                error: (error: unknown) => {
                  console.error(error);
                },
                finalize: () => {
                  patchState(store, hideLoading());
                },
              }),
            );
          }),
        ),
      ),

      /**
       * Registers a single upload, persists the file to local disk (named by its
       * uuid), opens the widget, then triggers the section-specific transfer.
       */
      registerUpload: rxMethod<RegisterUploadParams>(
        pipe(
          mergeMap(({ file, section, context }) => {
            const uuid = uuidv4();

            return uploadWidgetApiService.registerUpload({ uuid, fileName: file.name, section, context }).pipe(
              switchMap((response) => {
                const record: UploadWidgetItemDto = {
                  ...response.payload,
                  fileName: file.name,
                  section,
                  context,
                };

                patchState(store, { registeredUploads: [record, ...store.registeredUploads()] });

                return from(opfsService.writeFile(record.uuid, file)).pipe(
                  tap(() => {
                    toastService.showSuccess(translationService.translate('uploadWidget.registerSuccess'));
                    patchState(store, { isWidgetVisible: true });
                    store.startUpload(record);
                  }),
                );
              }),
              catchError((error: unknown) => {
                console.error(error);
                return EMPTY;
              }),
            );
          }),
        ),
      ),
    }),
  ),
  withHooks({
    onInit(store) {
      store.loadRecords();
    },
  }),
);
